import { yupResolver } from '@hookform/resolvers/yup'
import CategoryIcon from '@mui/icons-material/Category'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import QuizIcon from '@mui/icons-material/Quiz'
import {
  Box,
  Button,
  Container,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import DialogConfirm from 'components/Dialogs/DialogConfirm'
import CanvasArea from 'components/Quiz/CanvasArea'
import UploadImage from 'components/Quiz/UploadImage'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'
import { useAppDispatch } from 'app/hooks'
import { UIAction } from 'app/slices/UI'
import { useEffect } from 'react'

interface FormInput {
  name: string
  qText: string
  hint: string
}
const schema = yup.object({
  name: yup.string().required(),
  qText: yup.string(),
  hint: yup.string(),
})
export default function QuizRegister() {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [positions, setPositions] = useState<
    {
      x: number
      y: number
      size: number
    }[]
  >([])
  const [image, setImage] = useState<HTMLCanvasElement>()
  const [canvas, setCanvas] = useState<string>()
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {},
  })
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(
      UIAction.setBreadcrumbs([
        {
          text: 'ホーム',
          url: '/',
        },
        {
          text: '問題テンプレート管理',
          url: '/quiz',
        },
        {
          text: '新規登録',
          url: '/quiz/register',
        },
      ])
    )
  }, [dispatch])
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    setConfirm(true)
  }

  const handleRegister = async () => {
    if (!image) return
    try {
      setLoading(true)
      let formData = new FormData()
      await new Promise((rs) => {
        image.toBlob((blob) => {
          if (blob) {
            const ext = blob.type.split('/').pop()
            let file = new File([blob], 'fileName.' + ext, {
              type: blob.type,
            })
            formData.append('file', file)
            rs(1)
          }
        })
      })
      const { data: qImage } = await axiosClient.post(
        '/api/templates/upload',
        formData
      )
      const { name, hint, qText } = getValues()
      await axiosClient.post('/api/templates/register', {
        name,
        hint,
        qText,
        qImage,
        positions,
      })
      enqueueSnackbar('登録しました。', {
        variant: 'success',
      })
      setConfirm(false)
      navigate('/quiz')
    } catch (error) {
      enqueueSnackbar('エラーが発生しました。', {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }
  return (
    <Grid container spacing={1} mt={2}>
      <Grid item xs={12}>
        <Typography
          variant="h5"
          component="h2"
          fontWeight="bold"
          sx={{ borderBottom: 2 }}
        >
          新規登録
        </Typography>
      </Grid>

      <Grid item xs={12} ml={1}>
        <Typography variant="h6" component="h3" fontWeight="bold">
          問題
        </Typography>
        <Container
          maxWidth="md"
          disableGutters
          component="form"
          noValidate
          autoComplete="off"
        >
          <TextField
            required
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CategoryIcon color="primary" />
                </InputAdornment>
              ),
            }}
            label="テンプレートタイトル"
            fullWidth
            variant="standard"
            {...register('name')}
            error={'name' in errors}
          />
          <TextField
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QuizIcon color="primary" />
                </InputAdornment>
              ),
            }}
            label="問題文（任意）"
            fullWidth
            variant="standard"
            {...register('qText')}
            error={'qText' in errors}
          />
          <TextField
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LightbulbOutlinedIcon color="primary" />
                </InputAdornment>
              ),
            }}
            label="ヒント（任意）"
            fullWidth
            variant="standard"
            {...register('hint')}
            error={'hint' in errors}
          />
          <UploadImage
            setImage={(url) => {
              setImage(url)
              setPositions([])
              setCanvas('')
            }}
          />
          <CanvasArea
            imgPath={image?.toDataURL()}
            positions={positions}
            addPosition={(value) => {
              setPositions([...positions, value])
            }}
            clearPosition={() => setPositions([])}
            undoPosition={() => {
              const tmp = [...positions].slice(0, positions.length - 1)
              setPositions(tmp)
            }}
            handleFinish={(val) => setCanvas(val)}
          />
          <Button
            color="inherit"
            variant="outlined"
            sx={{
              px: 12,
              borderColor: '#aaa',
              borderRadius: 2,
              mt: 4,
            }}
            onClick={handleSubmit(onSubmit)}
          >
            登録
          </Button>
        </Container>
      </Grid>
      <Grid item xs={12} ml={1}></Grid>
      <DialogConfirm
        open={confirm && !!image}
        loading={loading}
        maxWidth="md"
        handleClose={() => setConfirm(false)}
        handleSubmit={handleRegister}
        title="登録内容の確認"
        primaryButtonText="登録する"
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
        >
          <Typography
            variant="body1"
            component="h4"
            sx={{
              width: 180,
              fontWeight: 'bold',
            }}
          >
            テンプレートタイトル 
          </Typography>
          <Typography
            variant="body1"
            component="h4"
            sx={{
              flex: 1,
            }}
            className="mb-4 md:mb-0"
          >
            {getValues('name')}
          </Typography>
        </Stack>
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
        >
          <Typography
            variant="body1"
            component="h4"
            sx={{
              width: 180,
              fontWeight: 'bold',
            }}
          >
            問題文
          </Typography>
          <Typography
            variant="body1"
            component="h4"
            sx={{
              flex: 1,
            }}
            className="mb-4 md:mb-0"
          >
            {getValues('qText')}
          </Typography>
        </Stack>
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
        >
          <Typography
            variant="body1"
            component="h4"
            sx={{
              width: 180,
              fontWeight: 'bold',
            }}
          >
            ヒント
          </Typography>
          <Typography
            variant="body1"
            component="h4"
            sx={{
              flex: 1,
            }}
            className="mb-4 md:mb-0"
          >
            {getValues('hint')}
          </Typography>
        </Stack>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
        >
          <Typography
            variant="body1"
            component="h4"
            sx={{
              width: 180,
              fontWeight: 'bold',
            }}
          >
            画像
          </Typography>
          <Box
            component="img"
            width={360}
            src={image?.toDataURL()}
            alt="画像"
            className="mb-4 md:mb-0"
            border={1}
          ></Box>
        </Stack>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
        >
          <Typography
            variant="body1"
            component="h4"
            sx={{
              width: 180,
              fontWeight: 'bold',
            }}
          >
            テンプレート画像
          </Typography>
          <Box
            component="img"
            width={360}
            src={canvas}
            alt="テンプレート"
            className="mb-4 md:mb-0"
            border={1}
          ></Box>
        </Stack>
      </DialogConfirm>
    </Grid>
  )
}
